import { MarketKeyByAsset, formatDollars, getDisplayAsset } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import UnderlineTabs from 'components/Button/UnderlineTabs'
import ChangePercent from 'components/ChangePercent'
import CurrencyIcon from 'components/Currency/CurrencyIcon'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { TabPanel } from 'components/Tab'
import { Body } from 'components/Text'
import { DASHBOARD_SECTION_HEIGHT_PX } from 'constants/ui'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectMarkPrices, selectMarkets } from 'state/futures/selectors'
import { PerpsProvider } from 'state/futures/types'
import { useAppSelector } from 'state/hooks'
import { selectPreviousDayPrices } from 'state/prices/selectors'

import { MarketTab } from './constants'

const MarketWatcher = memo(() => {
	const { t } = useTranslation()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const futuresMarkets = useAppSelector(selectMarkets)
	const pastRates = useAppSelector(selectPreviousDayPrices)
	const markPrices = useAppSelector(selectMarkPrices)
	const [marketActiveTab, setMarketActiveTab] = useState<MarketTab>(MarketTab.WATCHLIST)

	const markets = useMemo(() => {
		return futuresMarkets.map((market) => {
			const marketKey =
				perpsProvider === PerpsProvider.SNX_V3
					? getDisplayAsset(market.asset) ?? market.asset
					: market.marketKey
			const pastPrice = pastRates.find(
				(price) => price.synth === getDisplayAsset(market.asset)?.toUpperCase()
			)
			const marketPrice = markPrices[market.marketKey] ?? wei(0)

			return {
				marketKey,
				asset: market.asset,
				price: marketPrice,
				priceChange:
					pastPrice?.rate && marketPrice.gt(0)
						? marketPrice.sub(pastPrice?.rate).div(marketPrice)
						: wei(0),
			}
		})
	}, [futuresMarkets, perpsProvider, pastRates, markPrices])

	const marketTabs = useMemo(
		() => [
			{
				name: MarketTab.WATCHLIST,
				label: t('dashboard.overview.market-watcher.watchlist'),
				active: marketActiveTab === MarketTab.WATCHLIST,
				onClick: (): void => {
					setMarketActiveTab(MarketTab.WATCHLIST)
				},
				data: markets.slice(0, 4),
			},
			{
				name: MarketTab.TOP_MOVERS,
				label: t('dashboard.overview.market-watcher.top-movers'),
				active: marketActiveTab === MarketTab.TOP_MOVERS,
				onClick: (): void => {
					setMarketActiveTab(MarketTab.TOP_MOVERS)
				},
				data: markets.sort((a, b) => Number(b.priceChange) - Number(a.priceChange)).slice(0, 4),
			},
		],
		[marketActiveTab, markets, t]
	)

	return (
		<MarketWatcherContainer>
			<TabButtonsContainer>
				<UnderlineTabs options={marketTabs} />
			</TabButtonsContainer>
			{marketTabs.map(({ name, data }) => (
				<CollapsibleTabPanel
					key={name}
					collapsed={false}
					name={name}
					activeTab={marketActiveTab}
					fullHeight
				>
					<FlexDivCol rowGap="5px">
						{data.map(({ marketKey, asset, price, priceChange }) => {
							return (
								<MarketListRow
									key={marketKey}
									justifyContent="space-between"
									$colored={priceChange.gt(0)}
								>
									<LeftCell justifyContent="flex-start">
										<CurrencyIcon currencyKey={MarketKeyByAsset[asset]} height={18} width={18} />
										<Body color="primary">{marketKey}</Body>
									</LeftCell>
									<CenterCell mono>{formatDollars(price)}</CenterCell>
									<RightCell mono>
										<ChangePercent value={priceChange} decimals={2} className="change-pct" />
									</RightCell>
								</MarketListRow>
							)
						})}
					</FlexDivCol>
				</CollapsibleTabPanel>
			))}
		</MarketWatcherContainer>
	)
})

const LeftCell = styled(FlexDivRowCentered)`
	flex: 1;
`

const CenterCell = styled(Body)`
	flex: 1;
	text-align: center;
`

const RightCell = styled(Body)`
	flex: 1;
	text-align: right;
`

const MarketWatcherContainer = styled(FlexDivCol)`
	flex: 2;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	border-radius: 8px;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	min-width: 250px;
	height: ${DASHBOARD_SECTION_HEIGHT_PX};
	justify-content: flex-start;
	padding: 5px 20px;
	row-gap: 15px;
`

const CollapsibleTabPanel = styled(TabPanel).attrs({ fade: true })<{ collapsed: boolean }>`
	display: flex;
	flex-direction: column;
	flex: ${({ collapsed }) => (collapsed ? '0' : 'auto')};
	transition: flex 0.4s ease-in-out;
`

const MarketListRow = styled(FlexDivRowCentered)<{ $colored: boolean }>`
	padding: 5px;
	border-radius: 4px;
	background: ${(props) =>
		props.$colored
			? props.theme.colors.selectedTheme.newTheme.button.position.long.active.background
			: props.theme.colors.selectedTheme.newTheme.button.position.short.active.background}1a;
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

export default MarketWatcher
